import { useCallback } from 'react';
import { MIDASProject, Report, ColumnId, createReportId } from '../../../../types';
import { ReportActionData } from '../../ReportActions';
import logger from '../../../utils/logger';

interface UseReportOperationsProps {
  project: MIDASProject | null;
  reportManager: {
    addHistogramToReport?: (reportId: string, columnId: ColumnId, bins: number, datasetId?: string) => Promise<void>;
    addScatterPlotToReport?: (reportId: string, xColumn: ColumnId, yColumn: ColumnId, datasetId?: string) => Promise<void>;
    addTimeSeriesPlotToReport?: (reportId: string, dateColumn: ColumnId, valueColumn: ColumnId) => Promise<void>;
    addMultiTimeSeriesPlotToReport?: (reportId: string, dateColumn: ColumnId, valueColumns: ColumnId[]) => Promise<void>;
    addTextToReport?: (reportId: string, content: string, height: number) => Promise<void>;
    addStatisticsSummaryToReport?: (reportId: string, targetColumns: string[], datasetId: string) => Promise<void>;
    updateReportElement: (reportId: string, elementId: string, updates: Record<string, unknown>) => Promise<boolean>;
    createReportWithHistogram: (columnId: ColumnId, bins: number, reportName?: string) => Promise<string | undefined>;
    createReportWithScatterPlot: (xColumn: ColumnId, yColumn: ColumnId, reportName?: string) => Promise<string | undefined>;
    createReportWithTimeSeriesPlot: (dateColumn: ColumnId, valueColumn: ColumnId, reportName?: string) => Promise<string | undefined>;
    createReportWithMultiTimeSeriesPlot: (dateColumn: ColumnId, valueColumns: ColumnId[], reportName?: string) => Promise<string | undefined>;
    createReportWithStatisticsSummary?: (targetColumns: string[], datasetId: string, reportName?: string) => Promise<string | undefined>;
    createEmptyReport: (name: string, description?: string) => Promise<Report | null>;
    updateReportName: (reportId: string, newName: string) => Promise<boolean>;
    deleteReport: (reportId: string) => Promise<boolean>;
    selectedReportId: string | null;
    selectReport: (reportId: string) => void;
  };
  updateProjectState: (project: MIDASProject) => Promise<void>;
  openReportTab: (reportId: string) => void;
}

/**
 * レポート操作に関するロジックを管理するカスタムフック
 * - レポートへの要素追加
 * - レポートの更新、エクスポート、複製
 * - 新規レポートの作成
 */
export function useReportOperations({
  project,
  reportManager,
  updateProjectState,
  openReportTab
}: UseReportOperationsProps) {
  
  // 汎用的なレポート追加ハンドラー
  const handleAddToReportGeneric = useCallback(async (reportId: string, data: ReportActionData) => {
    if (!project) {
      logger.warn('[useReportOperations] プロジェクトが利用できません');
      return;
    }
    
    
    try {
      switch (data.type) {
        case 'histogram':
          if (data.histogramData && reportManager.addHistogramToReport) {
            await reportManager.addHistogramToReport(
              reportId,
              data.histogramData.columnName, // 既にColumnId型
              data.histogramData.bins,
              data.histogramData.dataset.id
            );
          }
          break;
          
        case 'scatter_plot':
          if (data.scatterPlotData && reportManager.addScatterPlotToReport) {
            await reportManager.addScatterPlotToReport(
              reportId,
              data.scatterPlotData.xColumn, // 既にColumnId型
              data.scatterPlotData.yColumn, // 既にColumnId型
              data.scatterPlotData.dataset.id
            );
          }
          break;
          
        case 'residual_plot':
          if (data.residualPlotData && reportManager.addScatterPlotToReport) {
            await reportManager.addScatterPlotToReport(
              reportId,
              data.residualPlotData.predictedColumn, // 既にColumnId型
              data.residualPlotData.residualColumn, // 既にColumnId型
              data.residualPlotData.dataset.id
            );
          }
          break;
          
        case 'time_series':
        case 'time_series_plot':
          if (data.timeSeriesData && reportManager.addTimeSeriesPlotToReport) {
            await reportManager.addTimeSeriesPlotToReport(
              reportId,
              data.timeSeriesData.dateColumn, // 既にColumnId型
              data.timeSeriesData.valueColumn // 既にColumnId型
            );
          }
          break;
          
        case 'multi_time_series':
        case 'multi_time_series_plot':
          if (data.multiTimeSeriesData && reportManager.addMultiTimeSeriesPlotToReport) {
            await reportManager.addMultiTimeSeriesPlotToReport(
              reportId,
              data.multiTimeSeriesData.dateColumn, // 既にColumnId型
              data.multiTimeSeriesData.valueColumns // 既にColumnId[]型
            );
          }
          break;
          
        case 'text':
          if (data.textData && reportManager.addTextToReport) {
            await reportManager.addTextToReport(
              reportId,
              data.textData.content,
              200 // デフォルトの高さ
            );
          }
          break;
          
        case 'linear_regression':
          if (data.linearRegressionData) {
            // 線形回帰要素を追加
            await reportManager.updateReportElement(reportId, 'new-element', data.linearRegressionData);
          }
          break;

        case 'statistics_summary':
          if (data.statisticsSummaryData && reportManager.addStatisticsSummaryToReport) {
            await reportManager.addStatisticsSummaryToReport(
              reportId,
              data.statisticsSummaryData.targetColumns,
              data.statisticsSummaryData.datasetId
            );
          }
          break;

        default:
          logger.warn('[useReportOperations] 未対応のレポートデータタイプ:', data.type);
      }
      
    } catch (error) {
      logger.error('[useReportOperations] レポート要素追加エラー:', error);
    }
  }, [project, reportManager]);

  // レポートの更新
  const handleUpdateReport = useCallback(async (reportId: string, updates: Partial<Report>) => {
    if (!project) return;
    
    const updatedReports = (project.reports || []).map(report => {
      if (report.id === reportId) {
        return {
          ...report,
          ...updates,
          metadata: {
            ...report.metadata,
            lastModified: new Date().toISOString()
          }
        };
      }
      return report;
    });
    
    const updatedProject: MIDASProject = {
      ...project,
      reports: updatedReports
    };
    
    await updateProjectState(updatedProject);
  }, [project, updateProjectState]);

  // レポートのエクスポート
  const handleExportReport = useCallback(async (reportId: string) => {
    try {
      // レポートエクスポート機能の実装（将来の拡張用）
      logger.info('[useReportOperations] Exporting report:', { reportId });
      // TODO: Implement report export functionality
    } catch (error) {
      logger.error('[useReportOperations] Failed to export report:', error);
    }
  }, []);

  // レポートの複製
  const handleDuplicateReport = useCallback(async (reportId: string) => {
    if (!project) return;
    
    const report = project.reports?.find(r => r.id === reportId);
    if (!report) {
      logger.error('[useReportOperations] Report not found:', reportId);
      return;
    }
    
    const newReport: Report = {
      ...report,
      id: createReportId(`report-${crypto.randomUUID()}`),
      name: `${report.name} (Copy)`,
      metadata: {
        ...report.metadata,
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      }
    };
    
    const updatedProject: MIDASProject = {
      ...project,
      reports: [...(project.reports || []), newReport]
    };
    
    await updateProjectState(updatedProject);
  }, [project, updateProjectState]);

  // 新しいレポートを作成して、そこにデータを追加するハンドラー
  const handleCreateNewReportGeneric = useCallback(async (data: ReportActionData) => {
    
    let reportId: string | undefined;
    
    try {
      switch (data.type) {
        case 'histogram':
          if (data.histogramData) {
            // data.titleがある場合はそれを使い、ない場合はフォールバック
            const reportTitle = data.title || `${data.histogramData.columnName} Distribution`;
            reportId = await reportManager.createReportWithHistogram(
              data.histogramData.columnName, // 既にColumnId型
              data.histogramData.bins,
              reportTitle
            );
          }
          break;
          
        case 'scatter_plot':
          if (data.scatterPlotData) {
            // data.titleがある場合はそれを使い、ない場合はフォールバック
            const reportTitle = data.title || `${data.scatterPlotData.xColumn} vs ${data.scatterPlotData.yColumn}`;
            reportId = await reportManager.createReportWithScatterPlot(
              data.scatterPlotData.xColumn, // 既にColumnId型
              data.scatterPlotData.yColumn, // 既にColumnId型
              reportTitle
            );
          }
          break;

        case 'time_series':
        case 'time_series_plot':
          if (data.timeSeriesData) {
            // data.titleがある場合はそれを使い、ない場合はフォールバック
            const reportTitle = data.title || `${data.timeSeriesData.valueColumn} over ${data.timeSeriesData.dateColumn}`;
            reportId = await reportManager.createReportWithTimeSeriesPlot(
              data.timeSeriesData.dateColumn, // 既にColumnId型
              data.timeSeriesData.valueColumn, // 既にColumnId型
              reportTitle
            );
          }
          break;
          
        case 'multi_time_series':
        case 'multi_time_series_plot':
          if (data.multiTimeSeriesData) {
            // data.titleがある場合はそれを使い、ない場合はフォールバック
            const reportTitle = data.title || `Multi-series over ${data.multiTimeSeriesData.dateColumn}`;
            reportId = await reportManager.createReportWithMultiTimeSeriesPlot(
              data.multiTimeSeriesData.dateColumn, // 既にColumnId型
              data.multiTimeSeriesData.valueColumns, // 既にColumnId[]型
              reportTitle
            );
          }
          break;

        case 'statistics_summary':
          if (data.statisticsSummaryData && reportManager.createReportWithStatisticsSummary) {
            const reportTitle = data.title || `Statistics Summary`;
            reportId = await reportManager.createReportWithStatisticsSummary(
              data.statisticsSummaryData.targetColumns,
              data.statisticsSummaryData.datasetId,
              reportTitle
            );
          }
          break;

        default:
          logger.warn('[useReportOperations] 未対応のレポートタイプ:', data.type);
      }
      
      if (reportId) {
        logger.info('[useReportOperations] 新しいレポートを作成しました:', {
          reportId,
          reportType: data.type
        });
        // レポートタブを開く
        openReportTab(reportId);
      } else {
        logger.error('[useReportOperations] レポート作成に失敗しました - reportIdが返されませんでした', {
          reportType: data.type,
          title: data.title
        });
      }
    } catch (error) {
      logger.error('[useReportOperations] レポート作成エラー:', error);
    }
  }, [reportManager, openReportTab]);

  return {
    handleAddToReportGeneric,
    handleUpdateReport,
    handleExportReport,
    handleDuplicateReport,
    handleCreateNewReportGeneric
  };
}